import { useEffect, useMemo, useState } from 'react'
import useSWR, { SWRConfiguration } from 'swr'
import { learningApi } from '../../../services/api'
import { IFiltersParams, IQuestion } from '../components/SearchQuestionsContent/types'
import { usePagination } from '../../../hooks/use-pagination'
import { toast } from 'react-toastify'
import { useLocalStorage } from 'react-use'

const ITEMS_PER_PAGE_MOBILE = 1

type TGetQuestionsRes = { docs: IQuestion[]; total?: number }

export function useGetQuestions(params: { isMobile?: boolean }) {
  const token = localStorage.getItem('token')

  const [orderBy, setOrderBy] = useState<'new' | 'old'>('new')
  const [allPages, setAllPages] = useState<TGetQuestionsRes[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState<IFiltersParams>({
    search: '',
    subjects: [],
    themes: [],
    topics: [],
    years: [],
    evaluatingInstitutions: [],
    includes: [{ canceled: false }],
    schooling: [],
    publicTenders: [],
    questionType: [],
    roles: [],
    graduation: [],
    myQuestions: [],
  })

  const pagination = usePagination({ total: totalPages })

  const [itemsPerPageState, setItemsPerPageState] = useLocalStorage(
    'use-get-questions--items-per-page',
    1
  )

  const mostRecent = orderBy === 'new'
  const itemsPerPage = params.isMobile ? ITEMS_PER_PAGE_MOBILE : itemsPerPageState

  const body = useMemo(
    () => ({
      filters: { ...filters, mostRecent },
      page: pagination.active,
      limit: itemsPerPage,
    }),
    [filters, pagination.active, itemsPerPage, mostRecent]
  )

  const key = token ? ['/question/filter', token, body] : null

  const swr = useSWR<TGetQuestionsRes>(key, fetcher, {
    ...configuration,
    onSuccess(data) {
      if (params.isMobile) {
        setAllPages((prev) => {
          const newState = [...prev]

          newState[pagination.active - 1] = data

          return newState
        })
      }
    },
  })

  const reset = () => {
    pagination.first()
    if (params.isMobile) setAllPages([])
  }

  useEffect(() => {
    if (swr.isLoading) return

    setTotalPages(Math.ceil(swr.data.total / itemsPerPage || 1))
  }, [swr.data.total, swr.isLoading, itemsPerPage])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [filters, itemsPerPage, orderBy, params.isMobile])

  return {
    swr,
    allPages,
    pagination,
    params: {
      filters,
      setFilters,

      itemsPerPage,
      setItemsPerPage: setItemsPerPageState,

      orderBy,
      setOrderBy,

      mostRecent,
    },
  }
}

const fetcher = ([url, token, body]: [url: string, token: string, body: Record<string, any>]) =>
  learningApi
    .post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res.data)

const configuration: SWRConfiguration = {
  fallbackData: { docs: [], total: 0 },
  shouldRetryOnError: true,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    if (retryCount > 3) {
      toast.error('Não foi possível filtrar as questões. Tente novamente mais tarde.')
    } else {
      setTimeout(() => revalidate({ retryCount }), 1000)
    }
  },
  keepPreviousData: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
}
